/**
 * Constraint-based BlockPropertiesTable writer — DYN-4 Phase 4a (Fix 5
 * symmetric emitter).
 *
 * Symmetric to `decodeConstraintPropertiesTable` in
 * `dxfBridge.dynamic.parser.tier2.ts`. Emits the AutoCAD 2018+
 * `ACDBBLOCKPROPERTIESTABLE` shape where a spreadsheet of column
 * definitions and row cells drives a set of BLOCKUSERPARAMETERs and
 * CONSTRAINT parameters. This is the shape АБИ1.dxf's ГОСТ 8239-89
 * ibeam block uses: a single UserVariable key column ("Двутавр") plus 8
 * output columns (h / b / s / t / R1 / r2 / Площадь / Масса) × 17 rows
 * (profile numbers 10…60).
 *
 * The existing legacy `writeBlockPropertiesTable` in
 * `dxfBridge.dynamic.writer.tier2.ts` emits the older BTABLE shape
 * (91 entries[], 92 0, 70 per-column type); we keep that writer for
 * round-trip fixtures that populate only `entries[]`. The new writer
 * below populates `columns[]` / `rows[]` / `keyColumn` when they are
 * present on the `BlockPropertiesTable`, letting both shapes coexist.
 *
 * Group-code layout (must match the parser exactly):
 *   0    ACDBBLOCKPROPERTIESTABLE
 *   5    <handle>
 *   330  <block record>
 *   100  AcDbBlockPropertiesTable
 *   1    <name>
 *   90   <columnCount>
 *   per column:
 *     91   <column index>
 *     92   <column kind>          (0=user-variable, 1=updated-distance,
 *                                  2=updated-angle, 3=updated-real,
 *                                  5=updated-string)
 *     301  <column class name>
 *     300  <display name>
 *     340  <link handle>          (BLOCKUSERPARAMETER or CONSTRAINT handle)
 *     170  <value type>           (0=real, 1=distance, 2=area,
 *                                  3=angle, 5=string)
 *   93   <rowCount>
 *   per row:
 *     90   <row index>
 *     170  <row state flag>
 *     300  <row key>
 *     per cell:
 *       170 <cell kind>           (5=string, everything else=numeric)
 *       140 <numeric value>  OR  300 <string value>
 */
import type { DxfDynamicBlockObject, DxfGroup } from "@dxf-ai-editor/dxf-io";

import type {
  BlockPropertiesTable,
  BlockPropertiesTableColumn,
  BlockPropertiesTableRow,
} from "./dynamicBlock";
import { pushGroup } from "./dxfBridge.dynamic.shared";

/** Same minimal writer context as the other sibling writers. */
export type BptWriterContext = {
  nextHandle: () => string;
  blockRecordHandle: string;
};

// ═══════════════════════════════════════════════════════════════════════════
// Encoding helpers — reverse of the parser's decode tables
// ═══════════════════════════════════════════════════════════════════════════

function columnKindToCode(
  kind: BlockPropertiesTableColumn["kind"],
): number | undefined {
  switch (kind) {
    case "user-variable":
      return 0;
    case "updated-distance":
      return 1;
    case "updated-angle":
      return 2;
    case "updated-real":
      return 3;
    case "updated-string":
      return 5;
    case "unknown":
    default:
      return undefined;
  }
}

function valueTypeToCode(
  vt: BlockPropertiesTableColumn["valueType"],
): number | undefined {
  switch (vt) {
    case "real":
      return 0;
    case "distance":
      return 1;
    case "area":
      return 2;
    case "angle":
      return 3;
    case "string":
      return 5;
    case "unknown":
    default:
      return undefined;
  }
}

/**
 * Emit a single constraint-based BPT column. Conventionally each column
 * carries all six group codes even when a field is empty — AutoCAD
 * always writes them and some downstream tools (including our own
 * parser when lenient mode is disabled) assume their presence.
 */
function emitColumn(groups: DxfGroup[], col: BlockPropertiesTableColumn): void {
  pushGroup(groups, 91, col.index);
  const kindCode = columnKindToCode(col.kind);
  if (kindCode !== undefined) pushGroup(groups, 92, kindCode);
  if (col.columnName) pushGroup(groups, 301, col.columnName);
  if (col.displayName) pushGroup(groups, 300, col.displayName);
  if (col.linkHandle) pushGroup(groups, 340, col.linkHandle);
  const vtCode = valueTypeToCode(col.valueType);
  if (vtCode !== undefined) pushGroup(groups, 170, vtCode);
}

/**
 * Emit a single row + all per-cell values. `columns` is passed so the
 * cell emission can consult the column's declared valueType to choose
 * between the numeric (140) and string (300) encoding paths.
 *
 * The parser reads:
 *   - The first 170 after the row marker = state flag.
 *   - The first 300 after that = row key.
 *   - Subsequent cells: 170 (cell type) + 140 OR 300.
 *
 * We mirror that layout exactly.
 */
function emitRow(
  groups: DxfGroup[],
  row: BlockPropertiesTableRow,
  columns: BlockPropertiesTableColumn[],
): void {
  pushGroup(groups, 90, row.index);
  pushGroup(groups, 170, row.flag);
  pushGroup(groups, 300, row.key);
  for (let c = 0; c < columns.length; c += 1) {
    const col = columns[c];
    if (!col) continue;
    const cell = row.values[c];
    const isString =
      col.valueType === "string" || typeof cell === "string";
    if (isString) {
      pushGroup(groups, 170, 5);
      pushGroup(groups, 300, typeof cell === "string" ? cell : String(cell ?? ""));
    } else {
      // Emit a value-type marker matching the column, defaulting to 1
      // (distance) when we don't have a better hint — this is the
      // signal AutoCAD uses for every numeric cell in the ibeam
      // fixture.
      const vtCode = valueTypeToCode(col.valueType) ?? 1;
      pushGroup(groups, 170, vtCode);
      const numeric = typeof cell === "number" ? cell : Number(cell ?? 0);
      pushGroup(groups, 140, Number.isFinite(numeric) ? numeric : 0);
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Top-level writer
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Emit the constraint-based form of `ACDBBLOCKPROPERTIESTABLE`. Returns
 * `undefined` when the table carries no constraint-based columns/rows
 * (legacy BTABLE fixtures) so the caller can fall back to the legacy
 * writer in `writer.tier2.ts`.
 *
 * The returned object carries the same handle / owner pair the parser
 * round-trips, plus the full raw group list the top-level OBJECTS
 * serializer emits verbatim.
 */
export function tryWriteConstraintBlockPropertiesTable(
  table: BlockPropertiesTable,
  ctx: BptWriterContext,
): DxfDynamicBlockObject | undefined {
  const columns = table.columns ?? [];
  const rows = table.rows ?? [];
  if (columns.length === 0 && rows.length === 0) return undefined;

  const groups: DxfGroup[] = [];
  // Prefer the handle the parser preserved — so round-trip keeps the
  // same reference chain — but fall back to a fresh one for synthetic
  // in-memory instances.
  const handle = table.handle || table.id || ctx.nextHandle();
  const name = table.name || "BlockPropertiesTable";

  pushGroup(groups, 0, "ACDBBLOCKPROPERTIESTABLE");
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, ctx.blockRecordHandle);
  pushGroup(groups, 100, "AcDbBlockPropertiesTable");
  pushGroup(groups, 1, name);

  // Column section
  pushGroup(groups, 90, columns.length);
  for (const col of columns) emitColumn(groups, col);

  // Row section
  pushGroup(groups, 93, rows.length);
  for (const row of rows) emitRow(groups, row, columns);

  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKPROPERTIESTABLE",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}
